import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  CustomFieldType,
  CreateCustomFieldRequest,
  CustomFieldValidation,
  FIELD_TYPE_CONFIGS
} from '@/types/customField';
import { FieldTypeSelector } from './FieldTypeSelector';
import { OptionListEditor } from './OptionListEditor';
import { ValidationRuleEditor } from './ValidationRuleEditor';

interface CreateFieldModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (data: CreateCustomFieldRequest) => Promise<void>;
  isCreating?: boolean;
}

export const CreateFieldModal: React.FC<CreateFieldModalProps> = ({
  isOpen,
  onClose,
  onCreated,
  isCreating = false,
}) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<CreateCustomFieldRequest>({
    name: '',
    fieldType: 'TEXT',
    description: '',
    options: [],
    validation: {},
    isActive: true,
    isSearchable: false,
    isFilterable: false,
  });
  const [error, setError] = useState<string | null>(null);

  const config = FIELD_TYPE_CONFIGS.find(c => c.type === formData.fieldType);
  const needsOptions = config?.supportsOptions || false;

  const handleClose = () => {
    setStep(1);
    setFormData({
      name: '',
      fieldType: 'TEXT',
      description: '',
      options: [],
      validation: {},
      isActive: true,
      isSearchable: false,
      isFilterable: false,
    });
    setError(null);
    onClose();
  };

  const handleSubmit = async () => {
    setError(null);

    // Validate
    if (!formData.name.trim()) {
      setError('Field name is required');
      return;
    }

    if (needsOptions && (!formData.options || formData.options.length === 0)) {
      setError('At least one option is required for this field type');
      return;
    }

    try {
      await onCreated(formData);
      handleClose();
    } catch (err: any) {
      setError(err.message || 'Failed to create field');
    }
  };

  const canProceed = () => {
    if (step === 1) return !!formData.fieldType;
    if (step === 2) return !!formData.name.trim();
    return true;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    Create Custom Field
                  </h2>
                  <p className="text-sm text-gray-500">
                    Step {step} of 3
                  </p>
                </div>
                <Button variant="ghost" size="sm" onClick={handleClose}>
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-4">
                {/* Step 1: Select Field Type */}
                {step === 1 && (
                  <div className="space-y-4">
                    <h3 className="text-sm font-medium text-gray-700">
                      Choose Field Type
                    </h3>
                    <FieldTypeSelector
                      selectedType={formData.fieldType}
                      onChange={(type) => setFormData({ ...formData, fieldType: type })}
                    />
                  </div>
                )}

                {/* Step 2: Basic Info */}
                {step === 2 && (
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700 block mb-1">
                        Field Name <span className="text-red-500">*</span>
                      </label>
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="e.g., Budget Range, Industry Type"
                      />
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-700 block mb-1">
                        Description
                      </label>
                      <Textarea
                        value={formData.description || ''}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Help text for this field"
                        rows={2}
                      />
                    </div>

                    {/* Options for dropdown/multi-select */}
                    {needsOptions && (
                      <div>
                        <label className="text-sm font-medium text-gray-700 block mb-2">
                          Options <span className="text-red-500">*</span>
                        </label>
                        <OptionListEditor
                          options={formData.options || []}
                          onChange={(options) => setFormData({ ...formData, options })}
                        />
                      </div>
                    )}

                    {/* Toggles */}
                    <div className="space-y-3 pt-4 border-t border-gray-200">
                      <label className="flex items-center justify-between cursor-pointer">
                        <span className="text-sm text-gray-700">Searchable</span>
                        <input
                          type="checkbox"
                          checked={formData.isSearchable || false}
                          onChange={(e) => setFormData({ ...formData, isSearchable: e.target.checked })}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                      </label>
                      <label className="flex items-center justify-between cursor-pointer">
                        <span className="text-sm text-gray-700">Filterable in List View</span>
                        <input
                          type="checkbox"
                          checked={formData.isFilterable || false}
                          onChange={(e) => setFormData({ ...formData, isFilterable: e.target.checked })}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                      </label>
                    </div>
                  </div>
                )}

                {/* Step 3: Validation */}
                {step === 3 && (
                  <div className="space-y-4">
                    <h3 className="text-sm font-medium text-gray-700">
                      Validation Rules
                    </h3>
                    <ValidationRuleEditor
                      fieldType={formData.fieldType}
                      rules={formData.validation || {}}
                      onChange={(validation) => setFormData({ ...formData, validation })}
                    />
                  </div>
                )}

                {/* Error Message */}
                {error && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm mt-4">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {error}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between p-4 border-t border-gray-200">
                <div>
                  {step > 1 && (
                    <Button
                      variant="outline"
                      onClick={() => setStep(step - 1)}
                      disabled={isCreating}
                    >
                      Back
                    </Button>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <Button variant="outline" onClick={handleClose} disabled={isCreating}>
                    Cancel
                  </Button>
                  {step < 3 ? (
                    <Button
                      onClick={() => setStep(step + 1)}
                      disabled={!canProceed()}
                    >
                      Next
                    </Button>
                  ) : (
                    <Button onClick={handleSubmit} disabled={isCreating}>
                      {isCreating ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                          Creating...
                        </>
                      ) : (
                        'Create Field'
                      )}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
